import { onMounted, onUnmounted, watch } from 'vue'

export function useMeta(metaData) {
  const defaultMeta = {
    title: 'Cybrgate Cryptoworld',
    description: 'Professionelles Kryptowährungs-Management und Rating Framework für sichere und effiziente Investitionen.',
    image: 'https://cmi.cybrgate.ch/CybrgateLogoWithFont.png',
    url: 'https://cmi.cybrgate.ch/',
    type: 'website',
    siteName: 'Cybrgate Cryptoworld'
  }

  let addedElements = []

  const updateMeta = (meta) => {
    const finalMeta = { ...defaultMeta, ...meta }
    
    // Clean up previously added elements
    addedElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element)
      }
    })
    addedElements = []

    // Update document title
    document.title = finalMeta.title

    // Update existing meta tags or create new ones
    const metaTags = [
      // SEO Meta Tags
      { name: 'description', content: finalMeta.description },
      { name: 'keywords', content: finalMeta.keywords || 'Kryptowährung, Bitcoin, Trading, Portfolio Management, Blockchain, Investment, Rating Framework, Cybrgate' },
      
      // Open Graph Meta Tags
      { property: 'og:title', content: finalMeta.title },
      { property: 'og:description', content: finalMeta.description },
      { property: 'og:image', content: finalMeta.image },
      { property: 'og:url', content: finalMeta.url },
      { property: 'og:type', content: finalMeta.type },
      { property: 'og:site_name', content: finalMeta.siteName },
      
      // Twitter Card Meta Tags
      { name: 'twitter:title', content: finalMeta.title },
      { name: 'twitter:description', content: finalMeta.description },
      { name: 'twitter:image', content: finalMeta.image },
      { name: 'twitter:url', content: finalMeta.url }
    ]

    metaTags.forEach(tag => {
      const selector = tag.name ? `meta[name="${tag.name}"]` : `meta[property="${tag.property}"]`
      let element = document.querySelector(selector)
      
      if (!element) {
        element = document.createElement('meta')
        if (tag.name) element.setAttribute('name', tag.name)
        if (tag.property) element.setAttribute('property', tag.property)
        document.head.appendChild(element)
        addedElements.push(element)
      }
      
      element.setAttribute('content', tag.content)
    })

    // Update canonical URL if provided
    if (finalMeta.canonical) {
      let canonicalElement = document.querySelector('link[rel="canonical"]')
      if (!canonicalElement) {
        canonicalElement = document.createElement('link')
        canonicalElement.setAttribute('rel', 'canonical')
        document.head.appendChild(canonicalElement)
        addedElements.push(canonicalElement)
      }
      canonicalElement.setAttribute('href', finalMeta.canonical)
    }

    // Update structured data if provided
    if (finalMeta.structuredData) {
      let structuredDataElement = document.querySelector('script[type="application/ld+json"]')
      if (!structuredDataElement) {
        structuredDataElement = document.createElement('script')
        structuredDataElement.setAttribute('type', 'application/ld+json')
        document.head.appendChild(structuredDataElement)
        addedElements.push(structuredDataElement)
      }
      structuredDataElement.textContent = JSON.stringify(finalMeta.structuredData)
    }
  }

  onMounted(() => {
    if (metaData.value) {
      updateMeta(metaData.value)
    }
  })

  if (metaData.value) {
    watch(metaData, (newMeta) => {
      updateMeta(newMeta)
    }, { immediate: true })
  }

  onUnmounted(() => {
    // Clean up when component is destroyed
    addedElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element)
      }
    })
  })

  return {
    updateMeta
  }
}

// Helper function to get current page URL
export function getCurrentUrl() {
  const baseUrl = 'https://cmi.cybrgate.ch'
  const currentPath = window.location.hash.replace('#', '')
  return `${baseUrl}${currentPath}`
}

// Meta data configurations for different pages
export const pageMetaConfig = {
  login: {
    title: 'Login - Cybrgate Cryptoworld',
    description: 'Melden Sie sich bei Ihrem Cybrgate Konto an, um auf Ihr Kryptowährungs-Portfolio und unsere professionellen Trading-Tools zuzugreifen.',
    keywords: 'Login, Anmelden, Cybrgate, Kryptowährung Trading, Portfolio Zugang',
    url: 'https://cmi.cybrgate.ch/#/pages/login',
    canonical: 'https://cmi.cybrgate.ch/login'
  },
  
  dashboard: {
    title: 'Dashboard - Cybrgate Cryptoworld',
    description: 'Überwachen Sie Ihr Kryptowährungs-Portfolio mit unserem professionellen Dashboard. Echtzeitdaten, Performance-Tracking und detaillierte Analysen.',
    keywords: 'Dashboard, Portfolio, Kryptowährung Performance, Trading Analytics, Cybrgate',
    url: 'https://cmi.cybrgate.ch/#/dashboard',
    canonical: 'https://cmi.cybrgate.ch/dashboard'
  },
  
  register: {
    title: 'Registrierung - Cybrgate Cryptoworld',
    description: 'Erstellen Sie Ihr kostenloses Cybrgate Konto und beginnen Sie mit professionellem Kryptowährungs-Management und Trading.',
    keywords: 'Registrierung, Konto erstellen, Cybrgate, Kryptowährung Trading, Kostenlos anmelden',
    url: 'https://cmi.cybrgate.ch/#/pages/register',
    canonical: 'https://cmi.cybrgate.ch/register'
  },
  
  'rating-framework': {
    title: 'Rating Framework - Cybrgate Cryptoworld',
    description: 'Nutzen Sie unser fortschrittliches Rating Framework für die Bewertung von Kryptowährungen und fundierte Investitionsentscheidungen.',
    keywords: 'Rating Framework, Kryptowährung Bewertung, Investment Analyse, Cybrgate, Blockchain Rating',
    url: 'https://cmi.cybrgate.ch/#/rf/dashboard-rf',
    canonical: 'https://cmi.cybrgate.ch/rating-framework'
  },
  
  'rating-new': {
    title: 'Neues Rating - Cybrgate Cryptoworld',
    description: 'Erstellen Sie neue Kryptowährungs-Ratings mit unserem professionellen Bewertungsframework und fortschrittlichen Analysewerkzeugen.',
    keywords: 'Neues Rating, Kryptowährung Bewertung, Rating erstellen, Investment Analyse, Cybrgate',
    url: 'https://cmi.cybrgate.ch/#/rf/rating',
    canonical: 'https://cmi.cybrgate.ch/rating/new'
  },
  
  'rating-database': {
    title: 'Rating Datenbank - Cybrgate Cryptoworld',
    description: 'Durchsuchen Sie unsere umfassende Datenbank von Kryptowährungs-Ratings und historischen Bewertungen für fundierte Entscheidungen.',
    keywords: 'Rating Datenbank, Kryptowährung Historie, Bewertungsarchiv, Investment Research, Cybrgate',
    url: 'https://cmi.cybrgate.ch/#/rf/database-rf',
    canonical: 'https://cmi.cybrgate.ch/rating/database'
  },
  
  'admin-users': {
    title: 'Benutzerverwaltung - Cybrgate Admin',
    description: 'Verwalten Sie Benutzerkonten, Zugriffsrechte und Portfolio-Einstellungen in der Cybrgate Admin-Konsole.',
    keywords: 'Benutzerverwaltung, Admin Panel, Kontoverwaltung, Cybrgate Administration',
    url: 'https://cmi.cybrgate.ch/#/admin/user',
    canonical: 'https://cmi.cybrgate.ch/admin/users'
  },
  
  'admin-portfolios': {
    title: 'Portfolio-Verwaltung - Cybrgate Admin',
    description: 'Überwachen und verwalten Sie alle Kunden-Portfolios mit detaillierten Analysen und Performance-Metriken.',
    keywords: 'Portfolio-Verwaltung, Admin Panel, Portfolio Analyse, Cybrgate Administration',
    url: 'https://cmi.cybrgate.ch/#/admin/portfolios',
    canonical: 'https://cmi.cybrgate.ch/admin/portfolios'
  },
  
  'admin-transactions': {
    title: 'Transaktionsverwaltung - Cybrgate Admin',
    description: 'Überwachen Sie alle Kryptowährungs-Transaktionen mit umfassenden Filter- und Analyseoptionen.',
    keywords: 'Transaktionsverwaltung, Admin Panel, Transaction Monitoring, Cybrgate Administration',
    url: 'https://cmi.cybrgate.ch/#/admin/transactions',
    canonical: 'https://cmi.cybrgate.ch/admin/transactions'
  },
  
  'admin-fees': {
    title: 'Gebührenverwaltung - Cybrgate Admin',
    description: 'Verwalten Sie Handelsgebühren, Provisionen und andere Kosten in der Cybrgate Admin-Konsole.',
    keywords: 'Gebührenverwaltung, Admin Panel, Trading Fees, Cybrgate Administration',
    url: 'https://cmi.cybrgate.ch/#/admin/fees',
    canonical: 'https://cmi.cybrgate.ch/admin/fees'
  },
  
  'admin-taxes': {
    title: 'Steuerberichte - Cybrgate Admin',
    description: 'Generieren Sie detaillierte Steuerberichte für Kryptowährungs-Transaktionen und Portfolio-Performance.',
    keywords: 'Steuerberichte, Admin Panel, Tax Reports, Kryptowährung Steuern, Cybrgate',
    url: 'https://cmi.cybrgate.ch/#/admin/taxes',
    canonical: 'https://cmi.cybrgate.ch/admin/taxes'
  },
  
  'change-password': {
    title: 'Passwort ändern - Cybrgate Cryptoworld',
    description: 'Ändern Sie Ihr Cybrgate Konto-Passwort sicher und einfach für optimale Kontosicherheit.',
    keywords: 'Passwort ändern, Kontosicherheit, Cybrgate, Sicherheitseinstellungen',
    url: 'https://cmi.cybrgate.ch/#/pages/change-pw',
    canonical: 'https://cmi.cybrgate.ch/change-password'
  }
}